// src/pages/admin/AdminFieldAssociations.jsx

import React, { useEffect, useState, useMemo } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner, useConfirm } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, ChevronDown, GripVertical, X } from 'lucide-react';

const DATATYPES = [
  { value: 'EXACT_MATCH',             label: 'Exact Match',             desc: 'Binary yes/no comparison' },
  { value: 'CATEGORICAL_ORDINAL',     label: 'Categorical - Ordinal',   desc: 'Ranked categories with defined order' },
  { value: 'CATEGORICAL_PROXIMITY',   label: 'Categorical - Proximity', desc: 'Categories with varying distances' },
  { value: 'CATEGORICAL_SET',         label: 'Categorical - Set',       desc: 'Set membership / overlap comparison' },
  { value: 'CONTINUOUS_NUMERIC',      label: 'Continuous Numeric',      desc: 'Numeric value on a scale' },
  { value: 'FREE_TEXT_SEMANTIC',      label: 'Free Text - Semantic',    desc: 'Compared via embeddings / similarity' },
];

const datatypeLabel = (val) => DATATYPES.find(d => d.value === val)?.label || val;

// ── FDTA Form Modal ──────────────────────────────────────
const FdtaModal = ({ open, onClose, onSave, initial, fields, saving }) => {
  const [name, setName] = useState('');
  const [side, setSide] = useState('CV');
  const [fieldKey, setFieldKey] = useState('');
  const [datatype, setDatatype] = useState('');
  const [ordinalBands, setOrdinalBands] = useState(['']);

  useEffect(() => {
    if (open) {
      if (initial) {
        setName(initial.name);
        setSide(initial.side);
        setFieldKey(initial.fieldKey);
        setDatatype(initial.datatype);
        setOrdinalBands(
          initial.datatype === 'CATEGORICAL_ORDINAL' && Array.isArray(initial.ordinalBands) && initial.ordinalBands.length
            ? initial.ordinalBands
            : ['']
        );
      } else {
        setName(''); setSide('CV'); setFieldKey(''); setDatatype(''); setOrdinalBands(['']);
      }
    }
  }, [open, initial]);

  if (!open) return null;

  const sideFields = fields[side] || [];
  const grouped = sideFields.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  const canSave = name.trim() && fieldKey && datatype
    && (datatype !== 'CATEGORICAL_ORDINAL' || ordinalBands.filter(b => b.trim()).length >= 2);

  const handleSave = () => {
    const payload = {
      name: name.trim(),
      side,
      fieldKey,
      datatype,
    };
    if (datatype === 'CATEGORICAL_ORDINAL') {
      payload.ordinalBands = ordinalBands.filter(b => b.trim());
    }
    onSave(payload);
  };

  const handleBandChange = (idx, val) => {
    const next = [...ordinalBands];
    next[idx] = val;
    setOrdinalBands(next);
  };
  const addBand = () => setOrdinalBands([...ordinalBands, '']);
  const removeBand = (idx) => setOrdinalBands(ordinalBands.filter((_, i) => i !== idx));
  const moveBand = (idx, dir) => {
    const next = [...ordinalBands];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setOrdinalBands(next);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{initial ? 'Edit' : 'Create'} Field Association</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}>
            <X size={18} />
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Name */}
          <div>
            <label className="form-label">Name <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Education Level (CV)" />
          </div>

          {/* Side */}
          <div>
            <label className="form-label">Side <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['CV', 'POSITION'].map(s => (
                <button key={s} type="button"
                  className={`btn ${side === s ? 'btn-primary' : 'btn-outline'}`}
                  style={{ flex: 1, fontSize: 12 }}
                  onClick={() => { setSide(s); setFieldKey(''); }}>
                  {s === 'CV' ? 'CV Side' : 'Position Side'}
                </button>
              ))}
            </div>
          </div>

          {/* Field */}
          <div>
            <label className="form-label">Field <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <select className="form-input" value={fieldKey} onChange={e => setFieldKey(e.target.value)}>
              <option value="">Select a field...</option>
              {Object.entries(grouped).map(([group, gFields]) => (
                <optgroup key={group} label={group}>
                  {gFields.map(f => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Datatype */}
          <div>
            <label className="form-label">Datatype <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span></label>
            <select className="form-input" value={datatype} onChange={e => setDatatype(e.target.value)}>
              <option value="">Select a datatype...</option>
              {DATATYPES.map(d => (
                <option key={d.value} value={d.value}>{d.label} — {d.desc}</option>
              ))}
            </select>
          </div>

          {/* Ordinal Bands */}
          {datatype === 'CATEGORICAL_ORDINAL' && (
            <div>
              <label className="form-label">
                Ordinal Bands (lowest to highest) <span style={{ color: '#CC0000', marginLeft: 2 }}>*</span>
              </label>
              <div style={{ fontSize: 11, color: '#777', marginBottom: 8 }}>
                Define the ranked categories in order. At least 2 bands required.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {ordinalBands.map((band, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: '#999', width: 20, textAlign: 'right', flexShrink: 0 }}>
                      {idx + 1}.
                    </span>
                    <input className="form-input" style={{ flex: 1, margin: 0 }}
                      value={band} onChange={e => handleBandChange(idx, e.target.value)}
                      placeholder={`Band ${idx + 1}`} />
                    <button type="button" title="Move up" disabled={idx === 0}
                      onClick={() => moveBand(idx, -1)}
                      style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer',
                        color: idx === 0 ? '#ccc' : '#777', padding: 2, fontSize: 14 }}>
                      &#9650;
                    </button>
                    <button type="button" title="Move down" disabled={idx === ordinalBands.length - 1}
                      onClick={() => moveBand(idx, 1)}
                      style={{ background: 'none', border: 'none', cursor: idx === ordinalBands.length - 1 ? 'default' : 'pointer',
                        color: idx === ordinalBands.length - 1 ? '#ccc' : '#777', padding: 2, fontSize: 14 }}>
                      &#9660;
                    </button>
                    {ordinalBands.length > 1 && (
                      <button type="button" title="Remove" onClick={() => removeBand(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CC0000', padding: 2 }}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addBand}
                style={{ marginTop: 6, fontSize: 12, color: '#3D80CC', background: 'none',
                  border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Plus size={12} /> Add Band
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Spinner size={14} /> : (initial ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────
export const AdminFieldAssociations = () => {
  const [fdtas, setFdtas] = useState([]);
  const [fields, setFields] = useState({ CV: [], POSITION: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterSide, setFilterSide] = useState('ALL');
  const { confirm, dialog } = useConfirm();

  const load = async () => {
    try {
      const [fdtaRes, fieldsRes] = await Promise.all([
        matchingModelAPI.getFdtas(),
        matchingModelAPI.getFields(),
      ]);
      setFdtas(fdtaRes.data.data);
      setFields(fieldsRes.data.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const fieldLabel = (side, key) => {
    const f = (fields[side] || []).find(f => f.key === key);
    return f ? f.label : key;
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await matchingModelAPI.updateFdta(editing.id, payload);
        toast.success('Field association updated');
      } else {
        await matchingModelAPI.createFdta(payload);
        toast.success('Field association created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (fdta) => {
    if (!await confirm('Delete Field Association', `Delete "${fdta.name}"? This cannot be undone.`)) return;
    try {
      await matchingModelAPI.deleteFdta(fdta.id);
      toast.success('Deleted');
      await load();
    } catch {}
  };

  const filtered = filterSide === 'ALL' ? fdtas : fdtas.filter(f => f.side === filterSide);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div className="page-content-wide">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 18 }}>Field Associations</h1>
        <button className="btn btn-primary" style={{ fontSize: 12 }}
          onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus size={14} style={{ marginRight: 4 }} /> New Association
        </button>
      </div>

      {/* Description */}
      <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
        Define associations between platform data fields and their datatypes. Each association specifies
        how a field's data should be treated when used in matching comparisons.
      </p>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {['ALL', 'CV', 'POSITION'].map(s => (
          <button key={s}
            className={`btn ${filterSide === s ? 'btn-primary' : 'btn-outline'}`}
            style={{ fontSize: 11, padding: '4px 12px' }}
            onClick={() => setFilterSide(s)}>
            {s === 'ALL' ? 'All' : s === 'CV' ? 'CV Side' : 'Position Side'}
            {' '}({s === 'ALL' ? fdtas.length : fdtas.filter(f => f.side === s).length})
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: 32, color: '#999' }}>
            No field associations yet. Click "New Association" to create one.
          </div>
        </div>
      ) : (
        <div className="section-box">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#0e4e95', color: 'white', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Side</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Field</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Datatype</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((fdta, idx) => (
                <tr key={fdta.id} style={{ borderBottom: '1px solid #eee', background: idx % 2 ? '#fafafa' : 'white' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    {fdta.name}
                    {fdta.datatype === 'CATEGORICAL_ORDINAL' && Array.isArray(fdta.ordinalBands) && (
                      <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                        Bands: {fdta.ordinalBands.join(' → ')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                      background: fdta.side === 'CV' ? '#E8F4FD' : '#FDF2E8',
                      color: fdta.side === 'CV' ? '#0e4e95' : '#8B4513',
                    }}>
                      {fdta.side}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', color: '#555' }}>{fieldLabel(fdta.side, fdta.fieldKey)}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ fontSize: 11, color: '#3D80CC' }}>{datatypeLabel(fdta.datatype)}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button title="Edit" onClick={() => { setEditing(fdta); setModalOpen(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3D80CC', padding: 4 }}>
                        <Pencil size={14} />
                      </button>
                      <button title="Delete" onClick={() => handleDelete(fdta)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CC0000', padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FdtaModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
        fields={fields}
        saving={saving}
      />
      {dialog}
    </div>
  );
};
